import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { sideNavLinks } from "../../constants/enum";
import { Input } from "@heroui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faHandPointRight,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";

function MenuExplorer() {
  const { menu } = useSelector((state) => state.auth);
  const { isMultipleCompany } = useSelector((state) => state.company);

  const [searchItem, setSearchItem] = useState("");
  const [filter, setFilter] = useState("All Menu");

  // ✅ Convert to Set
  const userMenuComponent = useMemo(() => {
    return new Set(
      menu?.userMenu?.map((item) =>
        item.controller !== null ? item.controller : item.menuname
      )
    );
  }, [menu?.userMenu]);

  // ✅ Restricted menus
  const restrictedMenus = [
    "Master",
    "DiamondParameter",
    "Stock",
    "Sale",
    "Purchase",
    "MixSlit",
    "Account",
    "PacketTransfer",
    "SoftwareSettings",
  ];

  const isAllowedMenu = (key) =>
    !isMultipleCompany || !restrictedMenus.includes(key);

  // ✅ Permission filter (FIX)
  const filterByPermission = (menuList) => {
    const recursive = (items) => {
      return items
        .map((item) => {
          let filteredSub = [];

          if (item.sub) {
            filteredSub = recursive(item.sub);
          }

          const hasAccess =
            userMenuComponent?.has(item.key) && isAllowedMenu(item.key);

          if (hasAccess || filteredSub.length > 0) {
            return {
              ...item,
              sub: filteredSub.length > 0 ? filteredSub : item.sub,
            };
          }

          return null;
        })
        .filter(Boolean);
    };

    return recursive(menuList);
  };

  // ✅ Search filter
  const filterMenu = (menuList, query) => {
    const lowerQuery = query.toLowerCase();

    const recursive = (items) => {
      return items
        .map((item) => {
          let filteredSub = [];

          if (item.sub) {
            filteredSub = recursive(item.sub);
          }

          const isMatch = item.name.toLowerCase().includes(lowerQuery);

          if (isMatch || filteredSub.length > 0) {
            return {
              ...item,
              sub: filteredSub.length > 0 ? filteredSub : item.sub,
            };
          }

          return null;
        })
        .filter(Boolean);
    };

    return recursive(menuList);
  };

  // ✅ FINAL DATA (permission + search)
  const finalMenus = useMemo(() => {
    let data = filterByPermission(sideNavLinks);

    if (searchItem) {
      data = filterMenu(data, searchItem);
    }

    if (filter !== "All Menu") {
      data = data.filter(
        (item) => item.name === filter || item.category === filter
      );
    }

    return data;
  }, [searchItem, filter, userMenuComponent]);

  // ✅ Top menus (same style logic as before)
  const singleMenus = finalMenus.filter((item) => !item.sub);
  const nestedMenus = finalMenus.filter((item) => item.sub);

  // ✅ Headings
  const menuHeadings = useMemo(() => {
    const headings = sideNavLinks
      .map((item) => item.name)
      .filter((c) => c && c.trim() !== "");
    return ["All Menu", ...Array.from(new Set(headings))];
  }, []);

  const handleNavigation = (path) => {
    if (path) window.open(path, "_blank", "noopener,noreferrer");
  };

  const renderNestedMenu = (items) =>
    items.map((item, idx) => (
      <div
        key={item.key || idx}
        className="bg-white border rounded-md shadow-sm hover:shadow-md transition h-full flex flex-col"
      >
        <div
          onClick={() => handleNavigation(item.url)}
          className={`text-sm bg-slate-50 font-semibold border-b p-1 mb-2 cursor-pointer hover:underline
          ${!item.sub ? "text-blue-600" : "text-blue-800"}`}
        >
          {item.name}
        </div>

        <div className="space-y-2 p-2 flex-1">
          {item.sub?.map((subItem, j) => (
            <div key={subItem.key || j}>
              <div
                onClick={() => handleNavigation(subItem.url)}
                className={`text-xs font-medium cursor-pointer hover:underline
                ${!subItem.sub ? "text-slate-600" : "text-red-600"}`}
              >
                <span className="mr-1">
                  {!subItem.url ? (
                    <FontAwesomeIcon
                      className="text-red-600"
                      icon={faHandPointRight}
                    />
                  ) : (
                    <FontAwesomeIcon
                      className="text-red-600"
                      icon={faArrowRight}
                    />
                  )}
                </span>
                {subItem.name}
              </div>

              {subItem.sub && (
                <div className="ml-3 mt-1 border-l border-gray-200 pl-2 space-y-1">
                  {subItem.sub.map((nestItem, k) => (
                    <div key={nestItem.key || k}>
                      <div
                        onClick={() => handleNavigation(nestItem.url)}
                        className={`text-xs cursor-pointer hover:text-orange-500
                        ${
                          !nestItem.sub
                            ? "text-gray-700"
                            : "text-blue-700 font-semibold bg-slate-50 p-1"
                        }`}
                      >
                        {nestItem.name}
                      </div>

                      {nestItem.sub && (
                        <div className="ml-3 border-l border-gray-100 pl-2 space-y-1">
                          {nestItem.sub.map((last, l) => (
                            <div
                              key={last.key || l}
                              onClick={() => handleNavigation(last.url)}
                              className="text-xs text-gray-500 hover:text-orange-500 cursor-pointer"
                            >
                              • {last.name}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    ));

  return (
    <>
      {/* 🔍 Filter + Search (UNCHANGED STYLE) */}
      <div className="flex flex-col gap-2 p-2 border-b-1 border-blue-200 bg-blue-50">
        <Input
          type="text"
          placeholder="Search Menu Items..."
          onChange={(e) => setSearchItem(e.target.value)}
          variant="light"
          size="sm"
          startContent={<FontAwesomeIcon icon={faSearch} />}
          className="w-full font-semibold bg-white border-1 border-gray-200 text-xs rounded-0"
        />

        <select
          className="w-full text-xs rounded border p-1"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        >
          {menuHeadings.map((heading, idx) => (
            <option key={idx} value={heading}>
              {heading}
            </option>
          ))}
        </select>
      </div>

      {/* 🔹 Top Row (UNCHANGED STYLE) */}
      {singleMenus.length > 0 && (
        <div className="flex gap-3">
          {singleMenus.map((item, idx) => (
            <div
              key={item.key || idx}
              onClick={() => handleNavigation(item.url)}
              className="bg-slate-100 text-blue-800 text-xs font-bold rounded-sm p-2 cursor-pointer text-center hover:bg-blue-200 transition"
            >
              {item.name}
            </div>
          ))}
        </div>
      )}

      {/* 🔹 Nested Menus */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 p-2">
        {nestedMenus.length > 0 ? (
          renderNestedMenu(nestedMenus)
        ) : (
          <div className="col-span-full text-center text-gray-400 text-sm">
            No menu found
          </div>
        )}
      </div>
    </>
  );
}

export default MenuExplorer;